import { formatDate } from '@tracsis/library/lib/helpers';
import {
	Button,
	ButtonGroup,
	Content,
	Image,
	Section,
	Table,
	YouTubeEmbed,
} from '@tracsis/library/src';
import { Download } from '@tracsis/library/src/components/Icon';
import { ThemeContext } from '@tracsis/library/src/context/ThemeContext';
import { graphql } from 'gatsby';
import React, { useContext } from 'react';
import { render } from 'storyblok-rich-text-react-renderer';

import * as styles from './ResourcePost.module.css';

const ResourcePost = ({
	title,
	subtitle,
	content,
	image,
	downloads,
	business_unit,
	last_updated,
	type,
	snippet,
}: {
	title: string;
	subtitle?: string;
	content: string;
	image: { filename: string; alt: string };
	downloads: { filename: string }[];
	business_unit: string;
	last_updated: Date;
	type: string;
	snippet?: React.ReactNode;
}) => {
	const { variant } = useContext(ThemeContext);

	// Lots of the legacy data imported into Storyblok is a single string with all the HTML
	const isString = typeof content === 'string';

	return (
		<Section>
			<Content>
				{snippet}
				<article className="post">
					<Section>
						<Content>
							<div className={styles.grid}>
								{image && (
									<div className={styles.image}>
										<Image
											image={image.filename}
											alt={image.alt}
											fit="contain"
										/>
									</div>
								)}
								<div className={styles.header}>
									{business_unit &&
										(variant !== 'altaccent' ? (
											<p className={styles.businessUnit}>{business_unit}</p>
										) : null)}
									<h1 className={styles.title}>{title}</h1>
									{subtitle && <p>{subtitle}</p>}
								</div>
								<div className={styles.content}>
									<p className={styles.meta}>
										{last_updated && (
											<span className={styles.date}>
												{formatDate(last_updated)}
											</span>
										)}
										{type && <span className={styles.type}>{type}</span>}
									</p>

									<div className="rhythm">
										{isString ? (
											<p dangerouslySetInnerHTML={{ __html: content }} />
										) : (
											render(content, {
												blokResolvers: {
													['table']: (props) => <Table {...props} />,
													['youtube_embed']: (props) => (
														<YouTubeEmbed {...props} />
													),
												},
											})
										)}
									</div>

									{downloads && (
										<div className={styles.download}>
											<h4>Downloads</h4>
											{downloads.map((item, key) => (
												<ButtonGroup
													align="left"
													spacing="lg"
													className={styles.downloadButton}
													key={key}
												>
													<Button to={item.filename} theme="inverted">
														Download{' '}
														{item.filename
															.substr(item.filename.lastIndexOf('.') + 1)
															.toUpperCase()}{' '}
														<Download />
													</Button>
												</ButtonGroup>
											))}
										</div>
									)}
								</div>
							</div>
						</Content>
					</Section>
				</article>
			</Content>
		</Section>
	);
};

export const query = graphql`
	query ($slug: String!) {
		posts: allStoryblokEntry(
			filter: { field_component: { eq: "resource" }, slug: { ne: $slug } }
			limit: 3
			sort: { field_date_string: DESC }
		) {
			totalCount
			edges {
				node {
					...resource
				}
			}
		}
	}
`;

export default ResourcePost;
